import { useLanguage } from '@/contexts/LanguageContext';
import { Check } from 'lucide-react';

interface ProgressStripProps {
  currentStep: number; // 1..5
  totalSteps?: number; // default 5
}

const STEP_LABEL_KEYS = [
  'landing.requestTreatment.stepLabels.verify',
  'landing.requestTreatment.stepLabels.personal',
  'landing.requestTreatment.stepLabels.contact',
  'landing.requestTreatment.stepLabels.social',
  'landing.requestTreatment.stepLabels.case',
] as const;

export function ProgressStrip({ currentStep, totalSteps = 5 }: ProgressStripProps) {
  const { t } = useLanguage();
  const pct = Math.round((currentStep / totalSteps) * 100);
  const currentLabel = t(STEP_LABEL_KEYS[currentStep - 1]);

  return (
    <div className="px-5 pt-4 pb-3 border-b border-gray-100">
      <div className="flex justify-between items-baseline mb-2">
        <div className="text-sm font-bold text-[#004250]">
          {currentStep} / {totalSteps} — {currentLabel}
        </div>
        <div className="text-xs text-muted-foreground">{pct}%</div>
      </div>
      <div className="flex gap-1">
        {Array.from({ length: totalSteps }).map((_, i) => (
          <div
            key={i}
            className={`flex-1 h-1.5 rounded-full transition-colors ${
              i < currentStep ? 'bg-[#7dda55]' : 'bg-gray-200'
            }`}
          />
        ))}
      </div>
      <div className="flex justify-between mt-2 text-[10px]">
        {STEP_LABEL_KEYS.map((key, i) => {
          const done = i < currentStep - 1;
          const active = i === currentStep - 1;
          return (
            <span
              key={key}
              className={`flex items-center gap-0.5 font-medium ${
                done || active ? 'text-[#004250]' : 'text-muted-foreground'
              }`}
            >
              {done && <Check className="h-2.5 w-2.5" />}
              {t(key)}
            </span>
          );
        })}
      </div>
    </div>
  );
}
